export type AddressLike = {
  address_1: string;
  address_2?: string | null;
  city: string;
  state: string;
  postal_code: string;
};

export function formatAddressLine1(address: AddressLike): string {
  return address.address_2
    ? `${address.address_1}, ${address.address_2}`
    : address.address_1;
}

export function formatAddressLine2(address: AddressLike): string {
  return `${address.city}, ${address.state} ${address.postal_code.slice(0, 5)}`;
}

export function formatAddress(address: AddressLike): string {
  return `${formatAddressLine1(address)}, ${formatAddressLine2(address)}`;
}

const compareToken = (token: string) =>
  token.toLowerCase().replace(/[^a-z0-9]/g, "");

export type AddressSegment = { text: string; differs: boolean };

/**
 * Splits `line` into spaced-separated segments, flagging the ones that don't
 * appear in `compareTo`.
 *
 * If none of the segments match, we don't flag any diffs since they're totally
 * incomparable.
 */
export function diffAddressLine(
  line: string,
  compareTo: string,
): AddressSegment[] {
  const counterpart = new Set(
    compareTo.split(/\s+/).map(compareToken).filter(Boolean),
  );

  const segments = line
    .split(/(\s+)/)
    .filter(Boolean)
    .map((text) => {
      const token = compareToken(text);
      return { text, differs: Boolean(token) && !counterpart.has(token) };
    });

  const tokens = segments.filter((segment) => compareToken(segment.text));
  if (tokens.length > 0 && tokens.every((segment) => segment.differs)) {
    return segments.map((segment) => ({ ...segment, differs: false }));
  }

  return segments;
}

/** Determines if an address has the essential fields filled out and non-empty. */
export function isFullAddress<T extends AddressLike>(
  addr: T | null | undefined,
): addr is T {
  return Boolean(
    addr?.address_1 && addr?.city && addr?.state && addr?.postal_code,
  );
}

export function zip5(postalCode: string): string {
  return postalCode.slice(0, 5);
}

export const US_STATES: { code: string; name: string }[] = [
  { code: "AL", name: "Alabama" },
  { code: "AK", name: "Alaska" },
  { code: "AZ", name: "Arizona" },
  { code: "AR", name: "Arkansas" },
  { code: "CA", name: "California" },
  { code: "CO", name: "Colorado" },
  { code: "CT", name: "Connecticut" },
  { code: "DE", name: "Delaware" },
  { code: "DC", name: "District of Columbia" },
  { code: "FL", name: "Florida" },
  { code: "GA", name: "Georgia" },
  { code: "HI", name: "Hawaii" },
  { code: "ID", name: "Idaho" },
  { code: "IL", name: "Illinois" },
  { code: "IN", name: "Indiana" },
  { code: "IA", name: "Iowa" },
  { code: "KS", name: "Kansas" },
  { code: "KY", name: "Kentucky" },
  { code: "LA", name: "Louisiana" },
  { code: "ME", name: "Maine" },
  { code: "MD", name: "Maryland" },
  { code: "MA", name: "Massachusetts" },
  { code: "MI", name: "Michigan" },
  { code: "MN", name: "Minnesota" },
  { code: "MS", name: "Mississippi" },
  { code: "MO", name: "Missouri" },
  { code: "MT", name: "Montana" },
  { code: "NE", name: "Nebraska" },
  { code: "NV", name: "Nevada" },
  { code: "NH", name: "New Hampshire" },
  { code: "NJ", name: "New Jersey" },
  { code: "NM", name: "New Mexico" },
  { code: "NY", name: "New York" },
  { code: "NC", name: "North Carolina" },
  { code: "ND", name: "North Dakota" },
  { code: "OH", name: "Ohio" },
  { code: "OK", name: "Oklahoma" },
  { code: "OR", name: "Oregon" },
  { code: "PA", name: "Pennsylvania" },
  { code: "PR", name: "Puerto Rico" },
  { code: "RI", name: "Rhode Island" },
  { code: "SC", name: "South Carolina" },
  { code: "SD", name: "South Dakota" },
  { code: "TN", name: "Tennessee" },
  { code: "TX", name: "Texas" },
  { code: "UT", name: "Utah" },
  { code: "VT", name: "Vermont" },
  { code: "VA", name: "Virginia" },
  { code: "WA", name: "Washington" },
  { code: "WV", name: "West Virginia" },
  { code: "WI", name: "Wisconsin" },
  { code: "WY", name: "Wyoming" },
];
